/**
 * First-person walk controller (NAV-04).
 *
 * Composes:
 *   - @react-three/drei PointerLockControls (mouse look + pointer-lock lifecycle)
 *   - @react-three/drei useKeyboardControls (WASD + arrow keys via Renderer's keyMap)
 *   - useCollision hook (per-axis sliding collision + NAV-07 boundary check)
 *   - useNavStore (reads/writes camera pose so rig swaps don't lose position)
 *
 * Per-frame algorithm:
 *   1. Read direction from keyboard state
 *   2. Rotate direction into camera's facing (so 'forward' is where you look)
 *   3. Zero Y (no flying in Phase 3)
 *   4. Scale by WALK_SPEED * dt and add to current position
 *   5. Lock Y to CAMERA_HEIGHT (flat ground — Phase 6 terrain raycast lifts this)
 *   6. Resolve proposed position via useCollision (walls + boundary)
 *   7. Write resolved pose back to Zustand (Pitfall 8 — pose survives rig swaps)
 *
 * Source: 03-RESEARCH.md Pattern 2 (with collision extracted to useCollision hook).
 */

import { useFrame, useThree } from '@react-three/fiber';
import { PointerLockControls, useKeyboardControls } from '@react-three/drei';
import { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { useCollision } from '../collision/useCollision';
import { useNavStore } from '../store/useNavStore';
import { CAMERA_HEIGHT, WALK_SPEED } from '../lib/const';

type WalkKey = 'forward' | 'backward' | 'left' | 'right';

export function WalkRig() {
  const { camera } = useThree();
  const resolveProposed = useCollision();
  const setCameraPose = useNavStore((s) => s.setCameraPose);
  const storedPos = useNavStore((s) => s.cameraPosition);
  const storedQuat = useNavStore((s) => s.cameraQuaternion);
  const velocity = useRef(new THREE.Vector3());
  const direction = useRef(new THREE.Vector3());
  const [, getKeys] = useKeyboardControls<WalkKey>();

  // Mount: restore pose from Zustand (Pitfall 8 — pose survives rig swaps)
  useEffect(() => {
    camera.position.set(
      storedPos[0],
      Math.max(storedPos[1], CAMERA_HEIGHT),
      storedPos[2]
    );
    camera.quaternion.set(storedQuat[0], storedQuat[1], storedQuat[2], storedQuat[3]);
    // Intentionally mount-only — subsequent updates come from useFrame write-back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((_, delta) => {
    // Clamp delta to avoid huge jumps after tab-away (swept collision helps too,
    // but this is belt + suspenders against teleport-through-walls).
    const dt = Math.min(delta, 0.05);
    const { forward, backward, left, right } = getKeys();
    const speed = WALK_SPEED * dt;

    direction.current.set(0, 0, 0);
    if (forward) direction.current.z -= 1;
    if (backward) direction.current.z += 1;
    if (left) direction.current.x -= 1;
    if (right) direction.current.x += 1;

    if (direction.current.lengthSq() === 0) {
      // No WASD input, but still persist pose — mouse-look changes quaternion
      setCameraPose(
        [camera.position.x, camera.position.y, camera.position.z],
        [
          camera.quaternion.x,
          camera.quaternion.y,
          camera.quaternion.z,
          camera.quaternion.w,
        ]
      );
      return;
    }

    direction.current.normalize().applyQuaternion(camera.quaternion);
    direction.current.y = 0; // no flying in Phase 3
    direction.current.normalize();

    velocity.current.copy(direction.current).multiplyScalar(speed);

    const proposed = camera.position.clone().add(velocity.current);
    proposed.y = CAMERA_HEIGHT; // lock to ground; Phase 6 terrain raycast lifts this

    const resolved = resolveProposed(camera.position, proposed);
    camera.position.copy(resolved);

    setCameraPose(
      [resolved.x, resolved.y, resolved.z],
      [
        camera.quaternion.x,
        camera.quaternion.y,
        camera.quaternion.z,
        camera.quaternion.w,
      ]
    );
  });

  return <PointerLockControls />;
}
